import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);

/* JWT non è più in localStorage — vive nel cookie httpOnly `fabri_token` (login classico)
   o `session_token` (Google OAuth). Manteniamo solo il profilo utente in cache locale per
   evitare un flash su reload; l'unica verità è sempre GET /auth/me. */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fabri_user") || "null"); } catch { return null; }
  });
  // `ready`: /auth/me è stato chiamato almeno una volta. Finché è false
  // le route protette NON devono montare componenti che chiamano endpoint protetti
  // (altrimenti si rischiano 403 se il profilo cached è stale, es. missing must_change_password).
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Verifica sempre lato server via cookie (withCredentials); se fallisce → clear cache
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        try { localStorage.setItem("fabri_user", JSON.stringify(r.data)); } catch {}
      })
      .catch(() => {
        setUser(null);
        try { localStorage.removeItem("fabri_user"); } catch {}
      })
      .finally(() => setReady(true));
  }, []); // eslint-disable-line

  const login = async (email, password, totpCode) => {
    setLoading(true);
    try {
      const payload = { email, password };
      if (totpCode) payload.totp_code = totpCode;
      const r = await api.post("/auth/login", payload);
      // Step: totp_required → l'UI deve prompt del codice
      if (r.data?.step === 'totp_required') {
        return { step: 'totp_required', ...r.data };
      }
      // cookie fabri_token è settato dal server (httpOnly). Salviamo solo il profilo.
      try { localStorage.setItem("fabri_user", JSON.stringify(r.data.user)); } catch {}
      setUser(r.data.user);
      return { step: 'ok', user: r.data.user, must_change_password: r.data.must_change_password, two_factor_enabled: r.data.two_factor_enabled };
    } finally { setLoading(false); }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/register", payload);
      try { localStorage.setItem("fabri_user", JSON.stringify(r.data.user)); } catch {}
      setUser(r.data.user);
      return r.data.user;
    } finally { setLoading(false); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* best effort */ }
    try { localStorage.removeItem("fabri_user"); localStorage.removeItem("fabri_token"); } catch {}
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, loading }}>
      {children}
    </AuthCtx.Provider>
  );
};

// Fallback sicuro: se il contesto non è ancora montato, non crashare mai l'app.
const AUTH_FALLBACK = {
  user: null,
  ready: false,
  loading: false,
  login: async () => { throw new Error("Auth non inizializzata"); },
  register: async () => { throw new Error("Auth non inizializzata"); },
  logout: () => {},
};

export const useAuth = () => useContext(AuthCtx) || AUTH_FALLBACK;
